import { AnyAction } from 'redux';
import { SupportedManufacturer, supportedManufacturers } from '../../../../../web/models/constants/getHelpConstants';
import { appContext } from '../../../../../web/models/getHelp/appContext';
import { getHelpLogger } from '../../../../../web/utils/getHelpLogger';
import { captureContentUpdate } from '../../../../../web/utils/oneDsUtil';
import { addGenevaLog, getUserContext } from '../../../../../web/utils/statelessUtil';
import { getString } from '../../../../../web/utils/stringLoader';
import * as actionTypes from '../../../../models/actionTypes';
import { UserInfo } from '../../../../models/apiModels';
import {
  HttpStatusCodeTooManyRequest,
  SourceApps,
  M365UpsellNonAgentModalityId,
  OutdatedDevicesAlertEnabledComponent,
  OutlookDotComAppVer,
  DialogMap,
  Dialog,
} from '../../../../models/constants/constants';
import {
  IModalitySectionState,
  initialModalitySectionState,
  ServiceRequestPage,
  ServiceRequestPageType,
} from '../../../../models/reduxState';
import {
  MicrosoftEmailRegex,
  getAgentModalitiesInBH,
  getAgentModalitiesOOBH,
  getUpsellMetaDataKey,
  deepCopyProperty,
  isGetHelp,
} from '../../../../utilities';
import { getCurrentServiceRequestPageType } from '../../../app/selectors/contactUsSelector';
import { WaitTimeResponseModel, NonAgentModality, NonAgentModalities, NonAgentModalityType } from '../../models/modalityModels';
import { AttachmentData, ExtensionAttributeKeys, FreelanceRequest, RequestModality, RaveMetaData } from '../../models/requestModels';

const handleSupportDataGrabSuccess = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    consolidateGetModalitiesAPI:
      !!action.supportSettingsConfig &&
      action.supportSettingsConfig.ConsolidateGetModalitiesAPI,
    localizedDaysOfWeek: !!action.supportSettingsConfig
      ? action.supportSettingsConfig.LocalizedDaysOfWeek
      : null,
    alchemyThemeRoutingEnabled: !!action.supportSettingsConfig && !!action.supportSettingsConfig.AlchemyThemeRoutingEnabled,
    alchemyThemeRoutingShowWaitTime: !!action.supportSettingsConfig && !!action.supportSettingsConfig.AlchemyThemeRoutingShowWaitTime,
    alchemyThemeRoutingShowHOOPs: !!action.supportSettingsConfig && !!action.supportSettingsConfig.AlchemyThemeRoutingShowHOOPs,
    hideChatOutlookDotCom: !!action.supportSettingsConfig && !!action.supportSettingsConfig.HideChatOutlookDotCom,
    enableEmailRemovalOutlookDotCom: !!action.supportSettingsConfig && !!action.supportSettingsConfig.EnableEmailRemovalOutlookDotCom,
    isC2CCountryCodeEnabled: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsC2CCountryCodeEnabled,
    isLiveChatEmailRequirementDisabled: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsLiveChatEmailRequirementDisabled,
    isAMCModalityEnabled: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsAMCModalityEnabled,
    openInAppOmnichannelChatInBrowser: !!action.supportSettingsConfig && !!action.supportSettingsConfig.OpenInAppOmnichannelChatInBrowser,
  };
};

const handleSocDataGrabSuccess = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  let draftRequest = state.draftRequest;
  if (
    (action.sourceApp === SourceApps.Teams || action.sourceApp === SourceApps.Teams2) &&
    !!action.supportSettingsConfig &&
    !!action.supportSettingsConfig.TeamsContactUsAllowedMetadataKeys &&
    Object.keys(action.supportSettingsConfig.TeamsContactUsAllowedMetadataKeys).length > 0
  ) {
    const userContextObj = getUserContext();
    const teamsContactUsAllowedMetadataKeys =
      action.supportSettingsConfig.TeamsContactUsAllowedMetadataKeys;
    const metaData = Object.keys(userContextObj)
      .filter((key: string) =>
        teamsContactUsAllowedMetadataKeys.hasOwnProperty(key)
      )
      .map((key: string) => {
        return {
          Key: teamsContactUsAllowedMetadataKeys[key],
          Value: userContextObj[key],
        };
      });
    draftRequest = updateDraftRequest(draftRequest, { MetaDatas: metaData });
  }
  return {
    ...state,
    consolidateGetModalitiesAPI:
      !!action.supportSettingsConfig &&
      action.supportSettingsConfig.ConsolidateGetModalitiesAPI,
    alchemyUnsupportedThemes:
      !!action.supportSettingsConfig &&
      !!action.supportSettingsConfig.AlchemyUnsupportedThemes
        ? action.supportSettingsConfig.AlchemyUnsupportedThemes
        : [],
    localizedDaysOfWeek: !!action.supportSettingsConfig
      ? action.supportSettingsConfig.LocalizedDaysOfWeek
      : null,
    /**
     * UnAuth intent graph is for smcivr1 only,
     * so we don't need SMCUnauthExperienceAlchemyAppName &
     * unAuthBranchingEnabled under handleSupportDataGrabSuccess, which is for InApp
     */
    SMCUnauthExperienceAlchemyAppName: !!action.supportSettingsConfig
      ? action.supportSettingsConfig.SMCUnauthExperienceAlchemyAppName
      : 'smcunauth',
    unAuthBranchingEnabled: !!action.supportSettingsConfig && action.supportSettingsConfig.UnAuthBranchingEnabled,
    alchemyThemeRoutingEnabled: !!action.supportSettingsConfig && !!action.supportSettingsConfig.AlchemyThemeRoutingEnabled,
    alchemyThemeRoutingShowWaitTime: !!action.supportSettingsConfig && !!action.supportSettingsConfig.AlchemyThemeRoutingShowWaitTime,
    alchemyThemeRoutingShowHOOPs: !!action.supportSettingsConfig && !!action.supportSettingsConfig.AlchemyThemeRoutingShowHOOPs,
    hideChatOutlookDotCom: !!action.supportSettingsConfig && !!action.supportSettingsConfig.HideChatOutlookDotCom,
    enableEmailRemovalOutlookDotCom: !!action.supportSettingsConfig && !!action.supportSettingsConfig.EnableEmailRemovalOutlookDotCom,
    userImpactOptions: !!action.supportSettingsConfig ? action.supportSettingsConfig.UserImpactOptions : null,
    userRoleOptions: !!action.supportSettingsConfig ? action.supportSettingsConfig.UserRoleOptions : null,
    isUserImpactAndRoleDropdownEnabled: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsUserImpactAndRoleDropdownEnabled,
    isC2CCountryCodeEnabled: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsC2CCountryCodeEnabled,
    draftRequest: draftRequest,
    isLiveChatEmailRequirementDisabled: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsLiveChatEmailRequirementDisabled,
    isAMCModalityEnabled: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsAMCModalityEnabled,
    isRegionalSettingsEnabled: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsRegionalSettingsEnabled,
    timeZones: !!action.supportSettingsConfig ? action.supportSettingsConfig.TimeZones : null,
    supportedLanguages: !!action.supportSettingsConfig ? action.supportSettingsConfig.SupportedLanguages : null,
  };
};

const handleInitializeAlchemyStateOnSessionStart = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
    const draftRequest = new FreelanceRequest();
    draftRequest.UserDescription = action.searchQuery;

    return {
        ...state,
        draftRequest: draftRequest,
        isPhoneNumberValid: true,
        isEmailValid: true,
    };
};

const handleInitializeModalityStateOnSessionStart = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    answerDeskContentModel: action.modalities,
    isCollapsedModalities: false,
    inModalities: false,
    isModalitiesSearchInProgress: false,
    isRenderingModalitySection: true,
  };
};

const handleFetchRedirectSignInLinkSuccess = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    redirectForSignInLink: action.redirectForSignInLink,
  };
};

const handleSetAuthInfoForTeams = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    authInfo: action.authInfo,
  };
};

const handleFetchUserInfo = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    inModalities: false,
  };
};

const handleFetchUserInfoSuccess = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  const userInfo = action.userInfo as UserInfo;
  return {
    ...state,
    isSignedIn: true,
    userInfo: userInfo,
    isAdmin: userInfo.IsAdmin,
    isOrgId: userInfo.Provider.toLowerCase() === 'orgid',
    isMSFT: MicrosoftEmailRegex.test(userInfo.MemberName),
    userInfoFetchEnded: true,
  };
};

const handlefetchUserInfoNotSignedIn = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    isSignedIn: false,
    userInfoFetchEnded: true,
    serviceRequestPages: [{
      isBackButtonPresent: false,
      serviceRequestPageType: ServiceRequestPageType.SignInSection,
    }],
  };
};

const handleFetchUserInfoError = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    isSignedIn: false,
    hasThirdPartyCookiesEnabled: false,
    userInfoFetchEnded: true,
  };
};

const handleGettingModalitiesComplete = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    isModalitiesSearchInProgress: false,
  };
};
const handleUpdateModalitiesSectionExpando = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    isCollapsedModalities: action.isCollapsedModalities,
  };
};

const handlegetBackgroundAuthUriSuccess = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    backgroundAuthUri: action.backgroundAuthUri,
  };
};

const handleGetInfoClientTokenRetrievalSuccess = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    clientTokenRetrievalInfoModel: action.clientTokenRetrievalInfoModel,
  };
};

const handleSetIsUserSigningIntoClientApp = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    isUserIsSigningIntoClientApp: action.spinnerStatus,
  };
};

const handleErrorOnSignIn = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    errorOnSignIn: action.errorOnSignIn,
    authInfo: null,
    isSignedIn: false,
    userInfo: null,
    isAdmin: false,
    isOrgId: false,
    isMSFT: false,
  };
};

const handleFetchUWPAuthInfoSuccess = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    authInfo: action.authInfo,
  };
};

const handleUserSignOut = (state: IModalitySectionState): IModalitySectionState => {
  return {
    ...state,
    authInfo: null,
    isSignedIn: false,
    userInfo: null,
    isAdmin: false,
    isOrgId: false,
    isMSFT: false,
    serviceRequestPages: [{
      isBackButtonPresent: false,
      serviceRequestPageType: ServiceRequestPageType.SignInSection,
    }],
  };
};

const handleAlchemySuccess = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  const draftRequest = { ...state.draftRequest };
  const extensionAttributes = { ...draftRequest.ExtensionAttributes };
  if (!!action && !!action.alchemyResults) {
    const alchemyResults = action.alchemyResults;
    extensionAttributes[ExtensionAttributeKeys.Theme] =
      (!!alchemyResults.Theme && !!alchemyResults.Theme.Name)
      ? alchemyResults.Theme.Name
      : '';
    extensionAttributes[ExtensionAttributeKeys.AimId] =
      !!alchemyResults.CorrelationId
      ? alchemyResults.CorrelationId
      : '';
    extensionAttributes[ExtensionAttributeKeys.BucketId] = (
        !!alchemyResults.Insights &&
        alchemyResults.Insights.length > 0 &&
        !!alchemyResults.Insights[0] &&
        !!alchemyResults.Insights[0].BucketId
      )
      ? alchemyResults.Insights[0].BucketId.toString()
      : '';
    draftRequest.ExtensionAttributes = extensionAttributes;
  }

  return {
    ...state,
    draftRequest: draftRequest,
  };
};

const handleAlchemyError = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  const draftRequest = { ...state.draftRequest };
  const extensionAttributes = { ...draftRequest.ExtensionAttributes };
  // init two fields to mimic normal experience when fallback AlchemyResult step-in
  extensionAttributes[ExtensionAttributeKeys.Theme] = action?.alchemyResults?.ThemeRoutingData?.AlchemyTheme ?? '';
  extensionAttributes[ExtensionAttributeKeys.AimId] = !!action ? appContext.sessionId : '';
  extensionAttributes[ExtensionAttributeKeys.BucketId] = '';
  draftRequest.ExtensionAttributes = extensionAttributes;
  return {
    ...state,
    draftRequest: draftRequest,
  };
};

const handleUnAuthSchemaFetchInit = (
  state: IModalitySectionState = initialModalitySectionState,
  _action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    isUnauthSchemaFetching: true,
  };
};

const handleUnAuthSchemaFetchSuccess = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    isUnauthSchemaFetching: false,
    unAuthSignInResult: action.payload,
  };
};

const handleUnAuthSchemaFetchError = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    isUnauthSchemaFetching: false,
    unAuthSignInResult: null,
  };
};

const handleUnAuthLinkClick = (
  state: IModalitySectionState = initialModalitySectionState,
  _action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    showUnAuthBranching: true,
  };
};

const handlefetchAvailableModalitiesV2 = (state: IModalitySectionState = initialModalitySectionState, action: AnyAction): IModalitySectionState => {
  const currentPageType: ServiceRequestPageType = getCurrentServiceRequestPageType(state);
  // For 'Refresh modality option' we do not want to push the same page again.
  // We do not want to generalize prohibiting same kind of page pushed again.As in future articles and dialogs could be stacked one after another.
  if (currentPageType !== ServiceRequestPageType.ModalityOptions) {
    const page: ServiceRequestPage = {
      isBackButtonPresent: true,
      serviceRequestPageType: ServiceRequestPageType.ModalityOptions,
    };

    return {
      ...state,
      isGettingAvailableModalities: true,
      serviceRequestPages: [...state.serviceRequestPages, page],
    };
  } else {
    return {
      ...state,
      isGettingAvailableModalities: true,
    };
  }
};

const updatePreferredTimeZone = ( state: IModalitySectionState = initialModalitySectionState, action: AnyAction): IModalitySectionState => {
  return {
    ...state,
    preferredTimeZone: action.preferredTimeZone,
  };
};

const updatePreferredLocale = ( state: IModalitySectionState = initialModalitySectionState, action: AnyAction): IModalitySectionState => {
  return {
    ...state,
    preferredLocale: action.preferredLocale,
  };
};

 const getOEMModalityIfAvailable = (alchemyTheme: string, l1: string, l2: string): NonAgentModality => {
  // Return OEM modality only if themes are hardware related
   const oemRelatedAlchemyThemes: string[] = ['devices and drivers', 'other', 'power and battery', 'third party'];
   const oemRelatedl1s: string[] = ['windows', 'other products'];
   const oemRelatedL2s: string[] = ['devices and drivers', 'other accessories', 'technical support', 'windows'];

   if (!!alchemyTheme && oemRelatedAlchemyThemes.includes(alchemyTheme.toLowerCase())) {
     if (!!l1 && oemRelatedl1s.includes(l1?.toLowerCase())) {
       if ((!!l2 && oemRelatedL2s.includes(l2?.toLowerCase())) || alchemyTheme.toLowerCase() === 'third party') {
        // Loop through list of allowed manufacturers keyphrase, If registry 'manufacturer' entry includes the keyphrase select that 'SupportedManufacturer' node.
        const foundMan: SupportedManufacturer = supportedManufacturers.find(manufacturer => {
          const foundIndex = manufacturer.supportedNames.findIndex(name => {
            return appContext.manufacturer?.toLowerCase().includes(name.toLowerCase());
          });
          return foundIndex > -1;
        });
        // In the selected manufacturer node, loop through the allowed appaths, if registry url includes the suported apppath, return the supported apppath, if not return first suported url.
        const foundLink: string =
          foundMan?.supportedAppPaths?.find(path => {
            return appContext.manufacturerLink.toLowerCase().includes(path.toLowerCase());
          }) ?? foundMan?.supportedUrls[0];

        if (foundMan && foundLink) {
            // Brook TODO
            // logOEMModalityQualification(foundMan.id, foundLink, true);

            const oemMo: NonAgentModality = {
              Id: foundMan.id,
              Title: getString(foundMan.title),
              Description: getString(foundMan.description),
              Link: foundLink,
              Type: NonAgentModalityType.OEM,
            };

            return oemMo;
        }
      }
    }
  }
  return null;
};

const handleFetchAvailableModalitiesV2Success = (state: IModalitySectionState = initialModalitySectionState, action: AnyAction): IModalitySectionState => {
  // In case of success check for eligibility of OEM modality addition.
  if (!!action && !!action.response) {
    const extensionAttributes = state.draftRequest?.ExtensionAttributes;

    const oemModality: NonAgentModality = isGetHelp() && !!extensionAttributes && getOEMModalityIfAvailable(extensionAttributes[ExtensionAttributeKeys.Theme], extensionAttributes[ExtensionAttributeKeys.ThemeL1], extensionAttributes[ExtensionAttributeKeys.ThemeL2]);
    if (!!oemModality) {
      if (!action.response.NonAgentModalities) {
        action.response.NonAgentModalities = { Modalities: [] };
      }
      action.response.NonAgentModalities?.Modalities?.push(oemModality);
    }
  }

  // Sort agent modalities so they are shown in order
  let isDfC = false;
  let returnedC2CModality: WaitTimeResponseModel[];
  if (!!action &&
    !!action.response &&
    !!action.response.AgentModalities &&
    !!action.response.AgentModalities.Modalities &&
    action.response.AgentModalities.Modalities.length > 0
  ) {
    // see if c2c was returned for PayPerIncident/AgentAssistSub scenario
    returnedC2CModality = action.response.AgentModalities.Modalities.filter((agentModality: WaitTimeResponseModel) => agentModality.Modality == RequestModality.C2C);
    // Filter out chat modality for OutlookDotCom flight
    const isOutlookDotCom: boolean = window.location.href.toLowerCase().indexOf(OutlookDotComAppVer) !== -1;
    if (isOutlookDotCom) {
      if (state.hideChatOutlookDotCom) {
        const agentModalitiesFilteredChatOut = action.response.AgentModalities.Modalities.filter((agentModality: WaitTimeResponseModel) => agentModality.Modality != RequestModality.Chat);
        action.response.AgentModalities.Modalities = agentModalitiesFilteredChatOut;
      }
      const isChatReturned: boolean = action.response.AgentModalities.Modalities.some((agentModality: WaitTimeResponseModel) => agentModality.Modality == RequestModality.Chat);
      // Hide email modality if chat is shown
      if (isChatReturned && state.enableEmailRemovalOutlookDotCom) {
        const agentModalitiesFilteredEmailOut = action.response.AgentModalities.Modalities.filter((agentModality: WaitTimeResponseModel) => agentModality.Modality != RequestModality.Email);
        action.response.AgentModalities.Modalities = agentModalitiesFilteredEmailOut;
      }
    }
    // If upsell non-agent modality is returned, always hide C2C regardless if Rave returns it or not.  SOC calls UST which is source of truth for consumer entitlement.
    // UST distinguishes between trial/paid while Rave's OLS call doesn't.
    const nonAgentModalities: NonAgentModalities = action.response.NonAgentModalities;
    if (!!nonAgentModalities && nonAgentModalities.Modalities.length > 0) {
      const isUpsellModalityReturned: boolean = nonAgentModalities.Modalities.some((nonAgentModality: NonAgentModality) => nonAgentModality.Id === M365UpsellNonAgentModalityId.m365Upsell);
      if (isUpsellModalityReturned) {
        const agentModalitiesFilteredC2COut = action.response.AgentModalities.Modalities.filter((agentModality: WaitTimeResponseModel) => agentModality.Modality != RequestModality.C2C);
        action.response.AgentModalities.Modalities = agentModalitiesFilteredC2COut;
      }
    }

    action.response.AgentModalities.Modalities
      .sort((a: WaitTimeResponseModel, b: WaitTimeResponseModel) => a.Modality - b.Modality);
    isDfC = action.response.IsDfC;
  }

  logModalityResponse(action, true);
  if (!!action && !!action.response && !!action.response.ShowAskCommunity) {
    logCommunityForumOption();
  }
  /**
  When user clicks on PayPerIncident/AgentAssistSub scenario, it will trigger getavailablemodality call and then go straight to c2c RequestCreation stage because
  we don't want to show/store the new modality response. This is because we already know the user wants to do PayPerIncident/AgentAssistSub C2C so no need to display the modalities.
  Also, in case user hits back button during PayPerIncident/AgentAssistSub c2c ticket creation, we want to show the original modality options with chat and PayPerIncident/AgentAssistSub nonagentmodality.
  */
  const upsellMetaDataKey = getUpsellMetaDataKey(state.draftRequest);
  if (!!upsellMetaDataKey && !!returnedC2CModality && returnedC2CModality.length > 0) {
    const c2CModality: WaitTimeResponseModel = returnedC2CModality[0];
    const properties: Partial<FreelanceRequest> = { Modality: c2CModality.Modality };
    if (c2CModality.WaitTime !== null && c2CModality.WaitTime !== undefined) {
      properties.ExtensionAttributes = {
        [ExtensionAttributeKeys.WaitTime]: c2CModality.WaitTime.toString(),
      };
    } else {
      properties.ExtensionAttributes = { [ExtensionAttributeKeys.WaitTime]: null };
    }

    // Next page after receiving pay per incident modality should be RequestCreation
    const page: ServiceRequestPage = {
      isBackButtonPresent: true,
      serviceRequestPageType: ServiceRequestPageType.RequestCreation,
    };

    return {
      ...state,
      isGettingAvailableModalities: false,
      draftRequest: updateDraftRequest(state.draftRequest, properties),
      serviceRequestPages: [...state.serviceRequestPages, page],
    };
  } else {
    const draftRequest = !!upsellMetaDataKey
      ? updateDraftRequest(state.draftRequest, { MetaDatas: [{ Key: upsellMetaDataKey, Value: 'false' }] })
      : state.draftRequest;
    return {
      ...state,
      modalityResponse: action.response,
      isGettingAvailableModalities: false,
      draftRequest: draftRequest,
      isDfC: isDfC,
    };
  }
};

const handlefetchAvailableModalitiesV2Error = ( state: IModalitySectionState = initialModalitySectionState, _action: AnyAction): IModalitySectionState => {
  logCommunityForumOption();
  return {
    ...state,
    modalityResponse: null,
    isGettingAvailableModalities: false,
  };
};

const logCommunityForumOption = (): void => {
  const contentUpdateLog = {
    content: {
      contentId: 'communityForumOption',
      contentName: 'communityForumOption',
      areaName: 'modalitiesSection',
      formnm: 'maxFunnel',
    },
    behavior: 160,
  };
  captureContentUpdate(contentUpdateLog);
};

const logModalityResponse = ( action: AnyAction, isSuccess: boolean): void => {
  const hasAgentModality = !!action && !!action.response && !!action.response.AgentModalities && !!action.response.AgentModalities.Modalities && action.response.AgentModalities.Modalities.length > 0;
  const hasNonAgentModality = !!action && !!action.response && !!action.response.NonAgentModalities && !!action.response.NonAgentModalities.Modalities && action.response.NonAgentModalities.Modalities.length > 0;
  const contentUpdateLog = {
    content: {
      contentId: 'alchemeThemeRoutingModalities',
      contentName: 'alchemeThemeRoutingModalities',
      areaName: 'modalitiesSection',
      formnm: 'maxFunnel',
      modalityResponse: {
        isSuccess: isSuccess,
        agentModalities: hasAgentModality ? JSON.stringify(getAgentModalitiesInBH(action.response.AgentModalities.Modalities)) : null,
        agentModalitiesOOBH: hasAgentModality ? JSON.stringify(getAgentModalitiesOOBH(action.response.AgentModalities.Modalities)) : null,
        nonAgentModalities: hasNonAgentModality ? JSON.stringify(action.response.NonAgentModalities.Modalities) : null,
      },
    },
    behavior: 160,
  };
  captureContentUpdate(contentUpdateLog);
};

const handleUpdateIsPhoneNumberValid = ( state: IModalitySectionState = initialModalitySectionState, action: AnyAction): IModalitySectionState => {
  return {
    ...state,
    isPhoneNumberValid: action.isPhoneNumberValid,
  };
};

const handleUpdateIsEmailValid = ( state: IModalitySectionState = initialModalitySectionState, action: AnyAction): IModalitySectionState => {
  return {
    ...state,
    isEmailValid: action.isEmailValid,
  };
};

const handleUpdateIsTitleValid = ( state: IModalitySectionState = initialModalitySectionState, action: AnyAction): IModalitySectionState => {
  return {
    ...state,
    isTitleValid: action.isTitleValid,
  };
};

const handleUpdateDraftRequest = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  const properties = action.properties;
  return {
    ...state,
    draftRequest: updateDraftRequest(state.draftRequest, properties),
  };
};

export const updateDraftRequest = (draftRequest: FreelanceRequest, properties: Partial<FreelanceRequest>): FreelanceRequest => {
  // deep copy the draftRequest object
  draftRequest = { ...draftRequest };
  const keys = Object.keys(properties);
  if (!!keys && keys.length > 0) {
    keys.forEach((k: string) => {
      if (k === 'ExtensionAttributes') {
        const previousExtensionAttributesProps = { ...draftRequest[k] };
        const newExtensionAttributesProps = properties[k];
        const propKeys = Object.keys(newExtensionAttributesProps);
        if (!!propKeys && propKeys.length > 0) {
          propKeys.forEach((propKey: string) => {
            previousExtensionAttributesProps[propKey] = newExtensionAttributesProps[propKey];
          });
        }
        draftRequest[k] = previousExtensionAttributesProps;
      } else if (k === 'MetaDatas') {
        // Make a deep copy of MetaDatas array
        const previousMetaDatasProps: RaveMetaData[] = deepCopyProperty(draftRequest[k]);
        const newMetaDatasProps = properties[k];
        if (!!newMetaDatasProps && newMetaDatasProps.length > 0) {
          newMetaDatasProps.forEach( (newMetaDatasProp: RaveMetaData) => {
            let previousMetaDatasPropIndex = -1;
            previousMetaDatasProps.some((previousMetaDatasProp: RaveMetaData, index: number) => {
              if (previousMetaDatasProp.Key === newMetaDatasProp.Key) {
                previousMetaDatasPropIndex = index;
                return true;
              } else {
                return false;
              }
            });
            if (previousMetaDatasPropIndex !== -1) {
              previousMetaDatasProps[previousMetaDatasPropIndex].Value = newMetaDatasProp.Value;
            } else {
              previousMetaDatasProps.push({ ...newMetaDatasProp });
            }
          });
        }
        draftRequest[k] = previousMetaDatasProps;
      } else if (k === 'Attachments' || k === 'Notes') {
        const deepCopyProperties = deepCopyProperty(properties[k]);
        draftRequest[k] = deepCopyProperties;
      } else {
        draftRequest[k] = properties[k];
      }
    });
  }
  return draftRequest;
};

const handleAddAttachment = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  if (!action.requestId) {
    const request = { ...state.draftRequest };
    const attachments = request.Attachments ? request.Attachments: [];
    const newAttachmentData = new AttachmentData();
    newAttachmentData.Name = action.name;
    // Need to add a empty string to make up the cswId since it's needed by the component's key
    newAttachmentData.CwsId = '';
    attachments.push(newAttachmentData);
    // In action GET_ATTACHMENT_UPLOAD_TOKEN_ERROR, UPLOAD_FILE_METADATA_ERROR, and UPLOAD_FILE_CHUNK_ERROR, the last attachmentData will be pop() since the upload fails.
    // In action UPLOAD_FILE_METADATA_SUCCESS, the last attachmentdata will be pop() to update.
    return {
      ...state,
      draftRequest: updateDraftRequest(state.draftRequest, { Attachments: attachments }),
    };
  } else {
    return state;
  }
};

const handleDeleteAttachment = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  if (!action.requestId) {
    const request = { ...state.draftRequest };
    const attachments = request.Attachments;
    const payloadCwsId: string = action.cwsId;
    const cwsId = payloadCwsId.toLowerCase();
    if (!payloadCwsId) {
      return state;
    }
    let removeIndex = -1;
    !!attachments && attachments.forEach((attachment: AttachmentData, index: number) => {
      if (!!attachment.CwsId && attachment.CwsId.toLowerCase() === cwsId) {
        removeIndex = index;
      }
    });
    if (removeIndex > -1) {
      attachments.splice(removeIndex, 1);
    }
    return {
      ...state,
      draftRequest: updateDraftRequest(state.draftRequest, { Attachments: attachments }),
    };
  } else {
    return state;
  }
};

const handleCancelUploadAttachment = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  if (!action.requestId) {
    const request = { ...state.draftRequest };
    const attachments = request.Attachments ? request.Attachments: [];
    // Need to be removed from the attachments array if the uploading process is canceled.
    if (!!attachments) {
      attachments.pop();
    }
    return {
      ...state,
      draftRequest: updateDraftRequest(state.draftRequest, { Attachments: attachments }),
    };
  } else {
    return state;
  }
};

const handleGetUploadAttachmentTokenError = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  if (!action.requestId) {
    const request = { ...state.draftRequest };
    const attachments = request.Attachments ? request.Attachments: [];
    // AttachmentData with Name and cwsId is added when addAttachmentAction is trigged, should be pop() if getUploadAttachmentToken api failed.
    if (!!attachments) {
      attachments.pop();
    }
    return {
      ...state,
      draftRequest: updateDraftRequest(state.draftRequest, { Attachments: attachments }),
    };
  } else {
    return state;
  }
};

const handleUploadFileMetadataError = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  if (!action.requestId) {
    const request = { ...state.draftRequest };
    const attachments = request.Attachments ? request.Attachments: [];
    // AttachmentData with Name and cwsId is added when addAttachmentAction is trigged, should be pop() if the uploadFileMetadata api failed.
    if (!!attachments) {
      attachments.pop();
    }
    return {
      ...state,
      draftRequest: updateDraftRequest(state.draftRequest, { Attachments: attachments }),
    };
  } else {
    return state;
  }
};

const handleUploadFileChunkError = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  if (!action.requestId) {
    const request = { ...state.draftRequest };
    const attachments = request.Attachments ? request.Attachments: [];
    //  AttachmentData with Name and cwsId is added when addAttachmentAction is trigged, should be pop() if the uploadFileChunk api failed.
    if (!!attachments) {
      attachments.pop();
    }
    return {
      ...state,
      draftRequest: updateDraftRequest(state.draftRequest, { Attachments: attachments }),
    };
  } else {
    return state;
  }
};

const handleUploadAttachmentSuccess = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  if (!action.requestId) {
    const request = { ...state.draftRequest };
    const attachments = request.Attachments ? request.Attachments: [];
    // AttachmentData with Name and cwsId is added when addAttachmentAction is trigged, should be pop() first to update.
    if (!!attachments) {
      attachments.pop();
    }
    attachments.push(action.attachmentData);
    return {
      ...state,
      draftRequest: updateDraftRequest(state.draftRequest, { Attachments: attachments }),
    };
  } else {
    return state;
  }
};

const handleUpdateDraftRequestWorkspaceId = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  if (!action.requestId) {
    let request = { ...state.draftRequest };
    request = updateDraftRequest(request, { WorkspaceId: action.workspaceId });
    return {
      ...state,
      draftRequest: request,
    };
  } else {
    return state;
  }
};

const handleUpdateBusinessImpactConfirmed = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    businessImpactConfirmed: action.businessImpactConfirmed,
  };
};

const handleUpdateCustomerAvailabilityConfirmed = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    customerAvailabilityConfirmed: action.customerAvailabilityConfirmed,
  };
};

const handleCreateRequest = (
  state: IModalitySectionState = initialModalitySectionState,
  _action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    isCreatingRequest: true,
    isModalityThrottled: false,
  };
};

const handleCreateRequestSuccess = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  // Rave cases start with 10, we only want to investigate smc2 cases still being created in Rave
  if (action?.request?.Source == "SMC2" && action?.request?.ParatureTicketNumber.startsWith('10')){
    const messageObj = {
      userAuthProvider: state.userInfo.Provider,
      tenantId: state.userInfo.TenantId,
      paratureTicketNumber: action.request.ParatureTicketNumber
    }
    const logHandlerTags = {
      content: {
        contentId: 'smc2ravecase',
        contentName: 'smc2ravecase',
        message: JSON.stringify(messageObj),
        url: `${window.location.href}`,
      },
      behavior: 'OTHER',
    };
    addGenevaLog(logHandlerTags);
  }
  return {
    ...state,
    draftRequest: new FreelanceRequest(),
    isPhoneNumberValid: true,
    isEmailValid: true,
    createdRequest: action.request,
    isCreatingRequest: false,
  };
};

const handleCreateRequestError = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  let isModalityThrottled = false;
  if (!!action.error && !!action.error.response && action.error.response.status === HttpStatusCodeTooManyRequest) {
    isModalityThrottled = true;
  }
  let request = state.draftRequest;
  request = updateDraftRequest(request, { Attachments: [] });
  return {
    ...state,
    isCreatingRequest: false,
    isModalityThrottled: isModalityThrottled,
    createdRequest: null,
    draftRequest: request,
  };
};

const handleRaveChatRequestCorrelationIdUpdate = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  return {
    ...state,
    autoOpenRaveChatRequestCorrelationId: action.correlationId,
  };
};

const handleDismissOutdatedDevicesAlert = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  if (action.callingComponent === OutdatedDevicesAlertEnabledComponent.ModalitySection) {
    return {
      ...state,
      isOutdatedDevicesAlertDismissed: true,
    };
  }
  return { ...state };
};

const handleWindowsUpdateAvailableForSA = (
  state: IModalitySectionState = initialModalitySectionState,
): IModalitySectionState => {
    return {
      ...state,
      isWindowsUpdateAvailableForSA: true,
    };
};

const handleDismissWindowsUpdateAvailableForSA = (
  state: IModalitySectionState = initialModalitySectionState,
): IModalitySectionState => {
    return {
      ...state,
      isWindowsUpdateAvailableForSA: false,
    };
};

const handleEndOfLifeMessageForSA = (
  state: IModalitySectionState = initialModalitySectionState,
): IModalitySectionState => {
  return {
    ...state,
    isEndOfLifeReachedForSA: true,
  };
};

const handleDismissEndOfLifeMessageForSA = (
  state: IModalitySectionState = initialModalitySectionState,
): IModalitySectionState => {
  return {
    ...state,
    isEndOfLifeReachedForSA: false,
  };
};

const handleToggleOutdatedDevicesAlertsExpand = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  if (action.callingComponent === OutdatedDevicesAlertEnabledComponent.ModalitySection) {
    return {
      ...state,
      shouldExpandOutdatedDevicesAlert: !state.shouldExpandOutdatedDevicesAlert,
    };
  }
  return { ...state };
};

const handlePushPageStack = (state: IModalitySectionState, action: AnyAction) => {
  return {
    ...state,
    serviceRequestPages: [...state.serviceRequestPages, action.page],
  };
};

const handlePopPageStack = (state: IModalitySectionState, action: AnyAction) => {
  // If perticular page needs to be reached then find it
  const pageFoundIndex = action?.purpose ? state.serviceRequestPages?.findIndex((page) => page.serviceRequestPageType === action.purpose) : null;
  // If NO perticular page needs to be reached then just pop the last page (if pagenot found index will be -1 and then we remove entire stack by slice 0)
  const count = pageFoundIndex ? pageFoundIndex +1 : (!!state.serviceRequestPages && state.serviceRequestPages.length > 0 ? state.serviceRequestPages.length-1: null);
  return {
    ...state,
    serviceRequestPages: (state.serviceRequestPages && count) ? state.serviceRequestPages.slice(0, count) : [],
  };
};

const emptyPageStack = (state: IModalitySectionState) => {
  return {
    ...state,
    serviceRequestPages: [] as ServiceRequestPage[],
  };
};

const handleUpdateShowAgentModalitiesButton = ( state: IModalitySectionState = initialModalitySectionState, action: AnyAction): IModalitySectionState => {
  return {
    ...state,
    showAgentModalitiesButtonClicked: action.showAgentModalitiesButtonClicked,
  };
};

const initializePageStack = (state: IModalitySectionState, action: AnyAction) => {
  return {
    ...state,
    serviceRequestPages: [action.page as ServiceRequestPage],
  };
};

const handleThemeRouting = (modalityState: IModalitySectionState, action: AnyAction): IModalitySectionState => {
  const dialogId: string = action.dialogId;

  const isDialogListed: boolean = dialogId in DialogMap;

  const hardcodedDialog: Dialog = isDialogListed ? DialogMap[dialogId] : null;

  if (isDialogListed) {
    const draftRequest: FreelanceRequest = modalityState.draftRequest;

    draftRequest.ExtensionAttributes[ExtensionAttributeKeys.Theme] = hardcodedDialog?.preselectedlevel0;

    draftRequest.ExtensionAttributes[ExtensionAttributeKeys.ThemeL1] = hardcodedDialog?.preselectedlevel1;

    draftRequest.ExtensionAttributes[ExtensionAttributeKeys.ThemeL2] = hardcodedDialog?.preselectedlevel2;

    draftRequest.ExtensionAttributes[ExtensionAttributeKeys.ThemeL3] = hardcodedDialog?.preselectedlevel3;
  }

  getHelpLogger.logCategoriesPageSkipOrNot(isDialogListed, dialogId, hardcodedDialog?.preselectedlevel0, hardcodedDialog?.preselectedlevel1, hardcodedDialog?.preselectedlevel2, hardcodedDialog?.preselectedlevel3);

  return { ...modalityState };
};

/**
 * Whenever you define a new property to IModalitySectionState, consider the need to reset it if it's impacted by search or ticket creation
 */
const handleSearchSessionReset = (
  state: IModalitySectionState = initialModalitySectionState
): IModalitySectionState => {
  return {
    ...state,
    draftRequest: initialModalitySectionState.draftRequest,
    isPhoneNumberValid: initialModalitySectionState.isPhoneNumberValid,
    isEmailValid: initialModalitySectionState.isEmailValid,
    answerDeskContentModel: initialModalitySectionState.answerDeskContentModel,
    isCollapsedModalities: initialModalitySectionState.isCollapsedModalities,
    inModalities: initialModalitySectionState.inModalities,
    isModalitiesSearchInProgress: initialModalitySectionState.isModalitiesSearchInProgress,
    isRenderingModalitySection: initialModalitySectionState.isRenderingModalitySection,
    isGettingAvailableModalities: initialModalitySectionState.isGettingAvailableModalities,
    hasThirdPartyCookiesEnabled: initialModalitySectionState.hasThirdPartyCookiesEnabled,
    errorOnSignIn: initialModalitySectionState.errorOnSignIn,
    isUnauthSchemaFetching: initialModalitySectionState.isUnauthSchemaFetching,
    showUnAuthBranching: initialModalitySectionState.showUnAuthBranching,
    modalityResponse: initialModalitySectionState.modalityResponse,
    isCreatingRequest: initialModalitySectionState.isCreatingRequest,
    createdRequest: initialModalitySectionState.createdRequest,
    autoOpenRaveChatRequestCorrelationId: initialModalitySectionState.autoOpenRaveChatRequestCorrelationId,
    isModalityThrottled: initialModalitySectionState.isModalityThrottled,
    shouldExpandOutdatedDevicesAlert: initialModalitySectionState.shouldExpandOutdatedDevicesAlert,
    serviceRequestPages: [],
  };
};

const handleUpdateSelectedEduProduct = (state: IModalitySectionState, action: AnyAction): IModalitySectionState => {
  return {
    ...state,
    eduProductId: action.eduProductId,
    draftRequest: updateDraftRequest(state.draftRequest, {
      ExtensionAttributes: {
        [ExtensionAttributeKeys.TaggedProduct]: `edu_${action.eduProductId}`
      }
    }),
  };
}

const handleAlchemyThemeRoutingSuccess = ( state: IModalitySectionState = initialModalitySectionState, action: AnyAction): IModalitySectionState => {
  return {
    ...state,
    isRenderingModalitySection: true
  }
}

export const ModalitySectionReducer = (
  state: IModalitySectionState = initialModalitySectionState,
  action: AnyAction
): IModalitySectionState => {
  switch (action.type) {
    case actionTypes.GET_EMERALD_SUPPORTCONFIG_SUCCESS:
      return handleSupportDataGrabSuccess(state, action);
    case actionTypes.GET_EMERALD_CONFIG_SUCCESS:
      return handleSocDataGrabSuccess(state, action);
    case actionTypes.INIT_SELF_HELP_ALCHEMY_ON_SESSION_START:
      return handleInitializeAlchemyStateOnSessionStart(state, action);
    case actionTypes.INIT_MODALITY_SECTION_ON_SESSION_START:
      return handleInitializeModalityStateOnSessionStart(state, action);
    case actionTypes.SET_AUTHINFO_FOR_TEAMS:
      return handleSetAuthInfoForTeams(state, action);
    case actionTypes.GET_ALCHEMY_THEME_ROUTING_DATA_SUCCESS:
      return handleAlchemyThemeRoutingSuccess(state, action);
    case actionTypes.FETCH_USERINFO:
      return handleFetchUserInfo(state, action);
    case actionTypes.FETCH_USERINFO_SUCCESS:
      return handleFetchUserInfoSuccess(state, action);
    case actionTypes.FETCH_USERINFO_NOT_SIGNEDIN:
      return handlefetchUserInfoNotSignedIn(state, action);
    case actionTypes.FETCH_USERINFO_ERROR:
      return handleFetchUserInfoError(state, action);
    case actionTypes.FETCH_REDIRECT_LINK_SUCCESS:
      return handleFetchRedirectSignInLinkSuccess(state, action);
    case actionTypes.HANDLE_GETTING_MODALITIES_COMPLETE:
      return handleGettingModalitiesComplete(state, action);
    case actionTypes.UPDATE_MODALITIES_SECTION_EXPANDO_STATE:
      return handleUpdateModalitiesSectionExpando(state, action);
    case actionTypes.FETCH_BACKGROUNDAUTH_URI_SUCCESS:
      return handlegetBackgroundAuthUriSuccess(state, action);
    case actionTypes.GET_INFO_CLIENT_TOKEN_RETRIEVAL_SUCCESS:
      return handleGetInfoClientTokenRetrievalSuccess(state, action);
    case actionTypes.SET_IS_USER_SIGNING_INTO_CLIENT_APP:
      return handleSetIsUserSigningIntoClientApp(state, action);
    case actionTypes.ERROR_ON_SIGN_IN:
      return handleErrorOnSignIn(state, action);
    case actionTypes.FETCH_UWP_AUTHINFO_SUCCESS:
      return handleFetchUWPAuthInfoSuccess(state, action);
    case actionTypes.USER_SIGN_OUT:
      return handleUserSignOut(state);
    case actionTypes.FETCH_ALCHEMY_SUCCESS:
      return handleAlchemySuccess(state, action);
    case actionTypes.FETCH_ALCHEMY_ERROR:
      return handleAlchemyError(state, action);
    case actionTypes.INIT_ALCHEMY_UNAUTH_ON_SESSION_START:
      return handleUnAuthSchemaFetchInit(state, action);
    case actionTypes.FETCH_UNAUTH_SCHEMA_SUCCESS:
      return handleUnAuthSchemaFetchSuccess(state, action);
    case actionTypes.FETCH_UNAUTH_SCHEMA_ERROR:
      return handleUnAuthSchemaFetchError(state, action);
    case actionTypes.TOGGLE_UNAUTH_BRANCHING:
      return handleUnAuthLinkClick(state, action);
    case actionTypes.FETCH_AVAILABLE_MODALITIES_V2:
      return handlefetchAvailableModalitiesV2(state, action);
    case actionTypes.FETCH_AVAILABLE_MODALITIES_V2_SUCCESS:
      return handleFetchAvailableModalitiesV2Success(state, action);
    case actionTypes.FETCH_AVAILABLE_MODALITIES_V2_ERROR:
      return handlefetchAvailableModalitiesV2Error(state, action);
    case actionTypes.UPDATE_PREFERRED_TIMEZONE:
      return updatePreferredTimeZone(state, action);
    case actionTypes.UPDATE_PREFERRED_LOCALE:
      return updatePreferredLocale(state, action);
    case actionTypes.UPDATE_IS_PHONENUMBER_VALID:
      return handleUpdateIsPhoneNumberValid(state, action);
    case actionTypes.UPDATE_IS_EMAIL_VALID:
      return handleUpdateIsEmailValid(state, action);
    case actionTypes.UPDATE_IS_TITLE_VALID:
      return handleUpdateIsTitleValid(state, action);
    case actionTypes.UPDATE_DRAFT_REQUEST:
      return handleUpdateDraftRequest(state, action);
    case actionTypes.ADD_ATTACHMENT:
      return handleAddAttachment(state, action);
    case actionTypes.DELETE_ATTACHMENT:
      return handleDeleteAttachment(state, action);
    case actionTypes.CANCEL_UPLOAD_ATTACHMENT:
      return handleCancelUploadAttachment(state, action);
    case actionTypes.GET_ATTACHMENT_UPLOAD_TOKEN_ERROR:
      return handleGetUploadAttachmentTokenError(state, action);
    case actionTypes.UPLOAD_FILE_METADATA_ERROR:
      return handleUploadFileMetadataError(state, action);
    case actionTypes.UPLOAD_FILE_CHUNK_ERROR:
      return handleUploadFileChunkError(state, action);
    case actionTypes.UPLOAD_ATTACHMENT_SUCCESS:
      return handleUploadAttachmentSuccess(state, action);
    case actionTypes.UPDATE_DRAFTREQUEST_WORKSPACE_ID:
      return handleUpdateDraftRequestWorkspaceId(state, action);
    case actionTypes.UPDATE_BUSINESS_IMPACT_CONFIRMED:
      return handleUpdateBusinessImpactConfirmed(state, action);
    case actionTypes.UPDATE_CUSTOMER_AVAILABILITY_CONFIRMED:
      return handleUpdateCustomerAvailabilityConfirmed(state, action);
    case actionTypes.CREATE_REQUEST:
      return handleCreateRequest(state, action);
    case actionTypes.CREATE_REQUEST_SUCCESS:
      return handleCreateRequestSuccess(state, action);
    case actionTypes.CREATE_REQUEST_ERROR:
      return handleCreateRequestError(state, action);
    case actionTypes.UPDATE_LAST_OPENED_RAVE_CHAT_REQUEST_CORRELATIONID:
      return handleRaveChatRequestCorrelationIdUpdate(state, action);
    case actionTypes.RESET_SEARCH_SESSION:
      return handleSearchSessionReset(state);
    case actionTypes.DISMISS_OUTDATED_DEVICES_ALERT:
      return handleDismissOutdatedDevicesAlert(state, action);
    case actionTypes.TOGGLE_OUTDATED_DEVICES_ALERT_EXPAND:
      return handleToggleOutdatedDevicesAlertsExpand(state, action);
    case actionTypes.PUSH_PAGE_STACK:
      return handlePushPageStack(state, action);
    case actionTypes.POP_PAGE_STACK:
      return handlePopPageStack(state, action);
    case actionTypes.EMPTY_PAGE_STACK:
      return emptyPageStack(state);
    case actionTypes.INITIALIZE_PAGE_STACK:
      return initializePageStack(state, action);
    case actionTypes.HANDLE_THEME_ROUTING:
      return handleThemeRouting(state, action);
    case actionTypes.UPDATE_SHOW_AGENT_MODALITIES_BUTTON:
      return handleUpdateShowAgentModalitiesButton(state, action);
    case actionTypes.UPDATE_SELECTED_EDU_PRODUCT:
      return handleUpdateSelectedEduProduct(state, action);
    case actionTypes.SHOW_WINDOWS_UPDATE_AVAILABLE_FOR_SA:
      return handleWindowsUpdateAvailableForSA(state);
    case actionTypes.DISMISS_WINDOWS_UPDATE_AVAILABLE_FOR_SA:
      return handleDismissWindowsUpdateAvailableForSA(state);
    case actionTypes.SHOW_END_OF_LIFE_MESSAGE_FOR_SA:
      return handleEndOfLifeMessageForSA(state);
    case actionTypes.DISMISS_END_OF_LIFE_MESSAGE_FOR_SA:
      return handleDismissEndOfLifeMessageForSA(state);
    default:
      return state;
  }
};
